import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getMe, logout as apiLogout, type User } from '../api/auth';
import { ApiClientError } from '../api/client';

type AuthContextValue = {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  refreshSession: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const sessionQuery = useQuery({
    queryKey: ['auth', 'session'],
    queryFn: getMe,
    retry: (count, error) => !(error instanceof ApiClientError && error.status === 401) && count < 1,
    staleTime: 60_000,
  });

  const refreshSession = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['auth', 'session'] });
  }, [queryClient]);

  const logout = useCallback(async () => {
    try { await apiLogout(); } finally {
      localStorage.removeItem('loopora.activeWorkspaceId');
      queryClient.clear();
      queryClient.setQueryData(['auth', 'session'], null);
    }
  }, [queryClient]);

  const user = sessionQuery.data?.user ?? null;
  const value = useMemo<AuthContextValue>(() => ({
    user,
    isLoading: sessionQuery.isLoading,
    isAuthenticated: Boolean(user),
    refreshSession,
    logout,
  }), [logout, refreshSession, sessionQuery.isLoading, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider');
  return value;
}
